import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, ArrowUpRight } from 'lucide-react';
import { linkCta } from '../lib/announcementLinks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

const SESSION_KEY = 'urgent_alerts_seen';
const fmtDate = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

const seenIds = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]'); } catch (e) { return []; }
};

// Shown once per app opening (browser session) for each active urgent message.
export const UrgentAnnouncementModal = ({ items = [], scope = 'staff', onOpen = null }) => {
  const [pending, setPending] = useState([]);

  useEffect(() => {
    const seen = seenIds();
    setPending(items.filter((a) => a.priority === 'urgent' && !seen.includes(`${scope}:${a.id}`)));
  }, [items, scope]);

  if (!pending.length) return null;

  const acknowledge = () => {
    const seen = new Set(seenIds());
    pending.forEach((a) => seen.add(`${scope}:${a.id}`));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen]));
    setPending([]);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && acknowledge()}>
      <DialogContent className="max-w-md overflow-hidden p-0 border-0" data-testid="urgent-alert-modal">
        <div className="relative px-6 pt-6 pb-5 text-white" style={{ backgroundImage: 'linear-gradient(120deg, #8C1D12 0%, #B42318 45%, #D4A24C 120%)' }}>
          <span className="ticker-shine pointer-events-none absolute inset-0" aria-hidden="true" />
          <DialogHeader className="relative text-left">
            <div className="flex items-center gap-3">
              <span className="relative flex w-12 h-12 items-center justify-center rounded-2xl bg-white/15 ring-2 ring-white/40 ticker-urgent">
                <AlertTriangle className="w-6 h-6" strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.25em] ticker-blink">Alerte urgente</p>
                <DialogTitle className="font-display text-2xl text-white">
                  {pending.length > 1 ? `${pending.length} messages importants` : 'Message important'}
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="text-white/80 pt-1">Merci de lire attentivement avant de continuer.</DialogDescription>
          </DialogHeader>
        </div>
        <div className="px-6 py-5 space-y-3 max-h-[50vh] overflow-y-auto">
          {pending.map((a) => (
            <div key={a.id} className="rounded-2xl border-l-4 border-[#B42318] bg-[#FDF1EF] px-4 py-3" data-testid={`urgent-alert-item-${a.id}`}>
              <p className="text-[15px] font-semibold text-[#2B3A2C] leading-snug break-words">{a.message}</p>
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500">
                <Clock className="w-3 h-3" /> Valable jusqu'au {fmtDate(a.ends_at)}
              </p>
              {a.link && onOpen && (
                <button type="button" onClick={() => { acknowledge(); onOpen(a.link); }}
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#B42318] px-3 py-1 text-xs font-bold text-white hover:bg-[#8C1D12] transition-colors"
                  data-testid={`urgent-alert-link-${a.id}`}>
                  {linkCta(a.link)} <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="px-6 pb-6">
          <Button onClick={acknowledge} className="w-full rounded-xl bg-[#B42318] hover:bg-[#8C1D12] text-white font-semibold" data-testid="urgent-alert-ack-btn">
            J'ai compris
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UrgentAnnouncementModal;
